<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatMoneyWithSymbol, formatPriceChange, priceChangeClass } from '@/lib/money'
import { sideConfig } from '@/lib/product'
import {
    INSIGHTS_SIDES,
    formatPct,
    sideConfigKey,
    type InsightsSide,
    type InsightsSideSummary,
    type InsightsSummary,
} from '@/lib/insights'
import InsightsInfoTip from '@/components/InsightsInfoTip.vue'

/**
 * The headline metrics #1–#5, one tile each, one row per side the customer trades.
 *
 * #4 and #5 carry their own "not enough history" guard, so a null there is that
 * message; a null Trend Average or % of launch means no listings in scope, "—". The
 * margin is one figure across both sides and is absent for a one-sided customer.
 */
const props = defineProps<{
    summary: InsightsSummary
    currencySymbol: string
}>()

const { t } = useI18n()

type Row = {
    side: InsightsSide
    /** Null reads "not enough history". */
    text: string | null
    class?: Record<string, boolean>
}

type Tile = { key: string; rows: Row[] } | { key: 'margin'; rows: null }

const rows = (format: (values: InsightsSideSummary) => Omit<Row, 'side'>): Row[] =>
    INSIGHTS_SIDES.flatMap((side) => {
        const values = props.summary[side]

        return values === null ? [] : [{ side, ...format(values) }]
    })

const tiles = computed((): Tile[] => [
    {
        key: 'trendAverage',
        rows: rows((values) => ({ text: formatMoneyWithSymbol(values.trend_average, props.currencySymbol) })),
    },
    ...(props.summary.margin === null ? [] : [{ key: 'margin' as const, rows: null }]),
    {
        key: 'pctOfLaunch',
        rows: rows((values) => ({ text: formatPct(values.pct_of_launch, 1) })),
    },
    {
        key: 'depreciation',
        rows: rows((values) => ({
            text: formatPriceChange(values.depreciation_60d.value),
            class: priceChangeClass(values.depreciation_60d.value),
        })),
    },
    {
        key: 'volatility',
        rows: rows((values) => ({
            text: values.volatility_60d.value === null ? null : formatPct(values.volatility_60d.value, 2),
        })),
    },
])

/**
 * Two rows on wide screens: the three level metrics, then the two 60-day ones a
 * third wider. Five tiles in one row left too little width for a label, a value
 * and "not enough history" side by side. Without the margin (one-sided customer)
 * the four tiles split two and two.
 */
const spanClass = (index: number): string => {
    if (tiles.value.length === 5) {
        return index < 3 ? 'lg:col-span-2' : 'lg:col-span-3'
    }

    return 'lg:col-span-3'
}
</script>

<template>
    <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        <div
            v-for="(tile, index) in tiles"
            :key="tile.key"
            class="flex flex-col gap-5 rounded-xl border bg-card p-6"
            :class="spanClass(index)"
        >
            <div class="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                {{ t(`insights.kpi.${tile.key}`) }}
                <InsightsInfoTip :title="t(`insights.kpi.${tile.key}`)" :text="t(`insights.tips.${tile.key}`)" />
            </div>

            <div v-if="tile.rows === null && summary.margin" class="space-y-1.5">
                <div class="text-3xl font-semibold tabular-nums">
                    {{ formatMoneyWithSymbol(summary.margin.value, currencySymbol) }}
                </div>
                <p v-if="summary.margin.value !== null" class="text-sm text-muted-foreground">
                    {{
                        t('insights.kpi.marginSub', {
                            pct: formatPct(summary.margin.pct_of_resale),
                            coverage: formatPct(summary.margin.coverage_pct),
                        })
                    }}
                </p>
            </div>

            <div v-else-if="tile.rows" class="space-y-3">
                <div v-for="row in tile.rows" :key="row.side" class="flex items-baseline justify-between gap-4">
                    <span class="flex items-center gap-2 text-sm whitespace-nowrap text-muted-foreground">
                        <span class="size-2 rounded-full" :class="row.side === 'resell' ? 'bg-resell' : 'bg-tradein'" />
                        {{ sideConfig[sideConfigKey(row.side)].label }}
                    </span>
                    <span v-if="row.text !== null" class="text-2xl font-semibold tabular-nums" :class="row.class">
                        {{ row.text }}
                    </span>
                    <span v-else class="text-sm whitespace-nowrap text-muted-foreground">{{
                        t('insights.kpi.notEnoughHistory')
                    }}</span>
                </div>
            </div>
        </div>
    </div>
</template>
